import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  limit,
  type Timestamp,
} from "firebase/firestore";
import { db } from "../firebase/client";
import { useAuth } from "../contexts/AuthContext";
import { readPlanId, readPlanStatus } from "../services/plan";
import {
  Activity,
  Crown,
  FlaskConical,
  Headphones,
  LayoutDashboard,
  LogOut,
  MessageSquare,
  Search,
  ShieldOff,
  Star,
  TrendingUp,
  UserCheck,
  UserMinus,
  Users,
  Wallet,
  Zap,
} from "lucide-react";
import LoginScreen from "./LoginScreen";
import AdminSupportBetaPanel from "./AdminSupportBetaPanel";
import AdminFinancePanel from "./AdminFinancePanel";
import { isAdminEmail } from "../utils/adminAccess";

// ─── Types ────────────────────────────────────────────────────────────────────

type AdminTab = "dashboard" | "support_beta" | "finance";

type UserRow = {
  uid: string;
  name: string;
  email: string;
  planTier: string;
  subscriptionStatus: string;
  isPremium: boolean;
  createdAt: Timestamp | null;
  lastSeen: Timestamp | null;
};

type TicketSummary = {
  id: string;
  userEmail: string | null;
  category: string | null;
  message: string;
  status: string;
  priority: string | null;
  createdAt: Timestamp | null;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatTs(ts: Timestamp | null): string {
  if (!ts) return "—";
  try {
    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit", month: "2-digit", year: "2-digit",
    }).format(ts.toDate());
  } catch { return "—"; }
}

function formatTsShort(ts: Timestamp | null): string {
  if (!ts) return "—";
  try {
    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit", month: "2-digit",
      hour: "2-digit", minute: "2-digit",
    }).format(ts.toDate());
  } catch { return "—"; }
}

function normalizePlan(tier: string): "free" | "trial" | "pro" | "premium" | "other" {
  const t = tier.toLowerCase().trim();
  if (t === "premium") return "premium";
  if (t === "pro") return "pro";
  if (t === "trial") return "trial";
  if (!t || t === "free" || t === "none") return "free";
  return "other";
}

function pct(n: number, total: number): string {
  if (!total) return "0%";
  return `${Math.round((n / total) * 100)}%`;
}

// ─── Stat Card ────────────────────────────────────────────────────────────────

const StatCard: React.FC<{
  label: string; value: number | string;
  icon: React.ReactNode; accent: string; sub?: string;
}> = ({ label, value, icon, accent, sub }) => (
  <div className={`rounded-2xl border bg-slate-900/70 px-4 py-3.5 flex items-center justify-between gap-3 ${accent}`}>
    <div className="min-w-0">
      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 truncate">{label}</p>
      <p className="text-2xl font-black text-white mt-0.5 leading-none">{value}</p>
      {sub && <p className="text-[11px] text-slate-500 mt-0.5">{sub}</p>}
    </div>
    <div className="shrink-0 opacity-75">{icon}</div>
  </div>
);

// ─── Plan Card ────────────────────────────────────────────────────────────────

const PlanCard: React.FC<{
  label: string; count: number; total: number;
  icon: React.ReactNode; bar: string; border: string;
}> = ({ label, count, total, icon, bar, border }) => {
  const p = total ? Math.round((count / total) * 100) : 0;
  return (
    <div className={`rounded-2xl border bg-slate-900/70 px-5 py-4 space-y-3 ${border}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="opacity-80">{icon}</div>
          <span className="text-sm font-black text-white">{label}</span>
        </div>
        <div className="text-right">
          <span className="text-2xl font-black text-white">{count}</span>
          <span className="text-[11px] text-slate-500 ml-1">{pct(count, total)}</span>
        </div>
      </div>
      <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-700 ${bar}`} style={{ width: `${p}%` }} />
      </div>
    </div>
  );
};

// ─── Support Summary ──────────────────────────────────────────────────────────

const SupportSummary: React.FC<{ onViewSupport: () => void }> = ({ onViewSupport }) => {
  const { user } = useAuth();
  const [tickets, setTickets] = useState<TicketSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, "supportTickets"), orderBy("createdAt", "desc"), limit(50));
    const unsub = onSnapshot(q, (snap) => {
      setTickets(snap.docs.map((d) => {
        const r = d.data();
        return {
          id: d.id,
          userEmail: r.userEmail ?? null,
          category: r.category ?? null,
          message: r.message ?? "",
          status: r.status ?? "open",
          priority: r.priority ?? null,
          createdAt: r.createdAt ?? null,
        };
      }));
      setLoading(false);
    }, () => setLoading(false));
    return () => unsub();
  }, [user]);

  const counts = useMemo(() => ({
    open: tickets.filter((t) => t.status === "open").length,
    in_review: tickets.filter((t) => t.status === "in_review").length,
    resolved: tickets.filter((t) => t.status === "resolved").length,
    critical: tickets.filter((t) => t.priority === "critical").length,
  }), [tickets]);

  const recent = tickets.slice(0, 5);

  const catLabel: Record<string, string> = {
    assistance: "Assistência", complaint: "Reclamação", suggestion: "Sugestão",
  };

  const statusCls: Record<string, string> = {
    open: "bg-cyan-500/15 text-cyan-300 border-cyan-500/20",
    in_review: "bg-amber-500/15 text-amber-300 border-amber-500/20",
    resolved: "bg-emerald-500/15 text-emerald-300 border-emerald-500/20",
  };
  const statusLabel: Record<string, string> = {
    open: "Aberto", in_review: "Em análise", resolved: "Resolvido",
  };

  return (
    <section className="rounded-2xl border border-slate-800/70 bg-slate-900/60 overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-800/60 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-black text-white">Resumo de Suporte</h2>
          <p className="text-[11px] text-slate-500 mt-0.5">{tickets.length} chamados totais</p>
        </div>
        <button
          onClick={onViewSupport}
          className="inline-flex items-center gap-1.5 rounded-xl border border-cyan-500/20 bg-cyan-500/8 hover:bg-cyan-500/12 px-3 py-1.5 text-xs font-bold text-cyan-300 transition-colors"
        >
          <Headphones className="w-3.5 h-3.5" />
          Ver Suporte Beta
        </button>
      </div>

      {/* Mini counters */}
      <div className="grid grid-cols-4 divide-x divide-slate-800/60">
        {[
          { label: "Abertos", count: counts.open, cls: "text-cyan-400" },
          { label: "Em análise", count: counts.in_review, cls: "text-amber-400" },
          { label: "Resolvidos", count: counts.resolved, cls: "text-emerald-400" },
          { label: "Críticos", count: counts.critical, cls: "text-red-400" },
        ].map((c) => (
          <div key={c.label} className="px-4 py-3 text-center">
            <p className={`text-xl font-black ${c.cls}`}>{c.count}</p>
            <p className="text-[10px] text-slate-500 mt-0.5">{c.label}</p>
          </div>
        ))}
      </div>

      {/* Last 5 tickets */}
      <div className="px-4 pb-3">
        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 pt-3 pb-2">Últimos chamados</p>
        {loading && <p className="text-xs text-slate-500 py-2">Carregando...</p>}
        {!loading && recent.length === 0 && (
          <p className="text-xs text-slate-500 py-2">Nenhum chamado ainda.</p>
        )}
        <div className="space-y-1.5">
          {recent.map((t) => (
            <div key={t.id} className="flex items-center gap-2 py-1.5 border-b border-slate-800/40 last:border-0">
              <span className={`shrink-0 inline-flex rounded-full border px-1.5 py-0.5 text-[9px] font-bold uppercase ${statusCls[t.status] ?? statusCls.open}`}>
                {statusLabel[t.status] ?? t.status}
              </span>
              <span className="text-[11px] text-slate-300 truncate flex-1">
                {t.userEmail ?? "—"} · {catLabel[t.category ?? ""] ?? t.category ?? "—"}
              </span>
              <span className="text-[10px] text-slate-600 shrink-0">{formatTsShort(t.createdAt)}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─── Recent Activity ──────────────────────────────────────────────────────────

const RecentActivity: React.FC<{ users: UserRow[] }> = ({ users }) => {
  const { user } = useAuth();
  const [recentTickets, setRecentTickets] = useState<TicketSummary[]>([]);

  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, "supportTickets"), orderBy("createdAt", "desc"), limit(5));
    const unsub = onSnapshot(q, (snap) => {
      setRecentTickets(snap.docs.map((d) => {
        const r = d.data();
        return { id: d.id, userEmail: r.userEmail ?? null, category: r.category ?? null, message: r.message ?? "", status: r.status ?? "open", priority: r.priority ?? null, createdAt: r.createdAt ?? null };
      }));
    });
    return () => unsub();
  }, [user]);

  // New users: last 7 sorted by createdAt
  const newUsers = useMemo(() => {
    return [...users]
      .filter((u) => u.createdAt)
      .sort((a, b) => {
        const ta = a.createdAt?.toMillis() ?? 0;
        const tb = b.createdAt?.toMillis() ?? 0;
        return tb - ta;
      })
      .slice(0, 5);
  }, [users]);

  type ActivityItem = { id: string; icon: React.ReactNode; text: string; sub: string; ts: number };

  const items: ActivityItem[] = useMemo(() => {
    const list: ActivityItem[] = [];
    for (const u of newUsers) {
      list.push({
        id: `user-${u.uid}`,
        icon: <UserCheck className="w-3.5 h-3.5 text-emerald-400" />,
        text: u.name !== "Sem nome" ? u.name : u.email || "Novo usuário",
        sub: `Cadastro · ${normalizePlan(u.planTier).toUpperCase()}`,
        ts: u.createdAt?.toMillis() ?? 0,
      });
    }
    for (const t of recentTickets) {
      list.push({
        id: `ticket-${t.id}`,
        icon: <MessageSquare className="w-3.5 h-3.5 text-cyan-400" />,
        text: t.userEmail ?? "Usuário desconhecido",
        sub: `Suporte · ${t.status === "open" ? "Aberto" : t.status === "in_review" ? "Em análise" : "Resolvido"}`,
        ts: t.createdAt?.toMillis() ?? 0,
      });
    }
    return list.sort((a, b) => b.ts - a.ts).slice(0, 10);
  }, [newUsers, recentTickets]);

  return (
    <section className="rounded-2xl border border-slate-800/70 bg-slate-900/60 overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-800/60 flex items-center gap-2">
        <Activity className="w-4 h-4 text-slate-400" />
        <h2 className="text-sm font-black text-white">Atividade recente</h2>
      </div>
      <div className="divide-y divide-slate-800/40">
        {items.length === 0 && (
          <p className="text-xs text-slate-500 px-5 py-4">Sem atividade registrada.</p>
        )}
        {items.map((item) => (
          <div key={item.id} className="flex items-center gap-3 px-5 py-2.5">
            <div className="shrink-0 w-6 h-6 rounded-full bg-slate-800/80 flex items-center justify-center">
              {item.icon}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[12px] font-semibold text-slate-200 truncate">{item.text}</p>
              <p className="text-[10px] text-slate-500">{item.sub}</p>
            </div>
            <span className="text-[10px] text-slate-600 shrink-0">
              {item.ts ? new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }).format(new Date(item.ts)) : "—"}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
};

// ─── User Table ───────────────────────────────────────────────────────────────

const PAGE_SIZE = 15;

const UserTable: React.FC<{ users: UserRow[] }> = ({ users }) => {
  const [search, setSearch] = useState("");
  const [filterPlan, setFilterPlan] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [page, setPage] = useState(0);

  const planOptions = ["all", "premium", "pro", "trial", "free"];
  const statusOptions = ["all", "active", "trialing", "inactive", "canceled", "expired"];

  const planLabel: Record<string, string> = { all: "Todos os planos", premium: "Premium", pro: "Pro", trial: "Trial", free: "Free" };
  const statusLabel: Record<string, string> = { all: "Todos os status", active: "Ativo", trialing: "Trial", inactive: "Inativo", canceled: "Cancelado", expired: "Expirado" };

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return users.filter((u) => {
      if (filterPlan !== "all") {
        const normalized = normalizePlan(u.planTier);
        // trial: aceitar tanto planTier "trial" quanto status "trialing"
        if (filterPlan === "trial") {
          if (normalized !== "trial" && u.subscriptionStatus !== "trialing") return false;
        } else if (normalized !== filterPlan) {
          return false;
        }
      }
      if (filterStatus !== "all" && u.subscriptionStatus !== filterStatus) return false;
      if (q && !u.name.toLowerCase().includes(q) && !u.email.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [users, search, filterPlan, filterStatus]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // Reset page when filters change
  const prevSearch = React.useRef(search);
  React.useEffect(() => { if (prevSearch.current !== search) { setPage(0); prevSearch.current = search; } }, [search]);

  const statusCls = (s: string) => {
    if (s === "active") return "text-emerald-400 font-semibold";
    if (s === "trialing") return "text-cyan-400";
    if (s === "canceled" || s === "expired") return "text-red-400";
    return "text-slate-500";
  };

  const planCls = (tier: string) => {
    const n = normalizePlan(tier);
    if (n === "premium") return "text-yellow-300 font-bold";
    if (n === "pro") return "text-cyan-300 font-semibold";
    if (n === "trial") return "text-blue-300 font-semibold";
    return "text-slate-400";
  };

  const selectCls = "bg-slate-800/70 border border-slate-700/60 text-slate-300 text-xs rounded-xl px-3 py-1.5 focus:outline-none focus:border-cyan-500/50";

  return (
    <section className="rounded-2xl border border-slate-800/70 bg-slate-900/60 overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-slate-800/60">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div>
            <h2 className="text-sm font-black text-white">Usuários</h2>
            <p className="text-[11px] text-slate-500 mt-0.5">
              {filtered.length} resultado{filtered.length !== 1 ? "s" : ""}
              {filtered.length !== users.length ? ` de ${users.length}` : ""}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-40">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500 pointer-events-none" />
            <input
              type="text" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              placeholder="Buscar nome ou email..."
              className="w-full bg-slate-800/70 border border-slate-700/60 text-slate-100 text-xs rounded-xl pl-8 pr-3 py-1.5 focus:outline-none focus:border-cyan-500/50 placeholder:text-slate-600"
            />
          </div>
          <select value={filterPlan} onChange={(e) => { setFilterPlan(e.target.value); setPage(0); }} className={selectCls}>
            {planOptions.map((p) => <option key={p} value={p}>{planLabel[p] ?? p}</option>)}
          </select>
          <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setPage(0); }} className={selectCls}>
            {statusOptions.map((s) => <option key={s} value={s}>{statusLabel[s] ?? s}</option>)}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-slate-800/60">
              {["Nome", "Email", "Plano", "Status", "Cadastro", "Último acesso"].map((h) => (
                <th key={h} className="px-4 py-2.5 text-left text-[10px] font-bold uppercase tracking-widest text-slate-500">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/40">
            {paginated.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">Nenhum usuário encontrado.</td></tr>
            )}
            {paginated.map((u) => (
              <tr key={u.uid} className="hover:bg-slate-800/30 transition-colors">
                <td className="px-4 py-2.5 font-medium text-slate-200 max-w-36 truncate">{u.name}</td>
                <td className="px-4 py-2.5 text-slate-400 max-w-44 truncate">{u.email || "—"}</td>
                <td className="px-4 py-2.5">
                  <span className={planCls(u.planTier)}>
                    {normalizePlan(u.planTier).toUpperCase()}
                  </span>
                </td>
                <td className={`px-4 py-2.5 ${statusCls(u.subscriptionStatus)}`}>
                  {u.subscriptionStatus || "—"}
                </td>
                <td className="px-4 py-2.5 text-slate-500">{formatTs(u.createdAt)}</td>
                <td className="px-4 py-2.5 text-slate-500">{formatTs(u.lastSeen)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-5 py-3 border-t border-slate-800/60 flex items-center justify-between">
          <p className="text-[11px] text-slate-500">
            Página {page + 1} de {totalPages}
          </p>
          <div className="flex gap-1">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}
              className="px-3 py-1 rounded-lg text-xs font-bold text-slate-300 hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              ← Anterior
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
              className="px-3 py-1 rounded-lg text-xs font-bold text-slate-300 hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              Próxima →
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

// ─── Dashboard Tab ────────────────────────────────────────────────────────────

const DashboardTab: React.FC<{ onNavigate: (tab: AdminTab) => void }> = ({ onNavigate }) => {
  const { user } = useAuth();
  const [allUsers, setAllUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    const unsub = onSnapshot(
      collection(db, "users"),
      (snapshot) => {
        setAllUsers(snapshot.docs.map((doc) => {
          const data = doc.data();
          const profile = data.profile || {};
          const planId = readPlanId(data);
          const planStatus = readPlanStatus(data);
          return {
            uid: doc.id,
            name: profile.name || data.name || "Sem nome",
            email: profile.email || data.email || "",
            planTier: planId,
            subscriptionStatus: planStatus,
            isPremium: data.isPremium === true || planId === "premium",
            createdAt: data.createdAt ?? null,
            lastSeen: data.lastSeen ?? data.updatedAt ?? null,
          };
        }));
        setLoading(false);
      },
      () => setLoading(false)
    );
    return () => unsub();
  }, [user]);

  const stats = useMemo(() => {
    const total = allUsers.length;
    const active = allUsers.filter((u) => u.subscriptionStatus === "active").length;
    const premium = allUsers.filter((u) => normalizePlan(u.planTier) === "premium").length;
    const pro = allUsers.filter((u) => normalizePlan(u.planTier) === "pro").length;
    const trial = allUsers.filter((u) => normalizePlan(u.planTier) === "trial" || u.subscriptionStatus === "trialing").length;
    const free = allUsers.filter((u) => normalizePlan(u.planTier) === "free").length;
    const inactive = allUsers.filter((u) => u.subscriptionStatus !== "active" && u.subscriptionStatus !== "trialing").length;
    return { total, active, premium, pro, trial, free, inactive };
  }, [allUsers]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-400 text-sm gap-2">
        <div className="w-4 h-4 border-2 border-slate-600 border-t-cyan-400 rounded-full animate-spin" />
        Carregando dados...
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* ─ 7 Metric cards ─ */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        <StatCard label="Total" value={stats.total} accent="border-slate-700/60"
          icon={<Users className="w-6 h-6 text-slate-400" />} />
        <StatCard label="Ativos" value={stats.active} accent="border-emerald-500/20"
          icon={<UserCheck className="w-6 h-6 text-emerald-400" />}
          sub={pct(stats.active, stats.total)} />
        <StatCard label="Premium" value={stats.premium} accent="border-yellow-500/20"
          icon={<Crown className="w-6 h-6 text-yellow-400" />}
          sub={pct(stats.premium, stats.total)} />
        <StatCard label="Pro" value={stats.pro} accent="border-cyan-500/20"
          icon={<Star className="w-6 h-6 text-cyan-400" />}
          sub={pct(stats.pro, stats.total)} />
        <StatCard label="Trial" value={stats.trial} accent="border-blue-500/20"
          icon={<FlaskConical className="w-6 h-6 text-blue-400" />}
          sub={pct(stats.trial, stats.total)} />
        <StatCard label="Free" value={stats.free} accent="border-slate-700/40"
          icon={<Zap className="w-6 h-6 text-slate-400" />}
          sub={pct(stats.free, stats.total)} />
        <StatCard label="Inativos" value={stats.inactive} accent="border-slate-700/40"
          icon={<UserMinus className="w-6 h-6 text-slate-500" />}
          sub={pct(stats.inactive, stats.total)} />
      </div>

      {/* ─ Plan cards ─ */}
      <div>
        <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-2">Distribuição por plano</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <PlanCard label="Premium" count={stats.premium} total={stats.total}
            icon={<Crown className="w-5 h-5 text-yellow-400" />}
            bar="bg-yellow-400" border="border-yellow-500/15" />
          <PlanCard label="Pro" count={stats.pro} total={stats.total}
            icon={<TrendingUp className="w-5 h-5 text-cyan-400" />}
            bar="bg-cyan-400" border="border-cyan-500/15" />
          <PlanCard label="Trial" count={stats.trial} total={stats.total}
            icon={<FlaskConical className="w-5 h-5 text-blue-400" />}
            bar="bg-blue-400" border="border-blue-500/15" />
          <PlanCard label="Free" count={stats.free} total={stats.total}
            icon={<Zap className="w-5 h-5 text-slate-400" />}
            bar="bg-slate-500" border="border-slate-700/50" />
        </div>
      </div>

      {/* ─ Support + Activity ─ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SupportSummary onViewSupport={() => onNavigate("support_beta")} />
        <RecentActivity users={allUsers} />
      </div>

      {/* ─ User table (full width) ─ */}
      <UserTable users={allUsers} />
    </div>
  );
};

// ─── Main AdminPanel ──────────────────────────────────────────────────────────

const AdminPanel: React.FC = () => {
  const { user, loading, logout } = useAuth();
  const [activeTab, setActiveTab] = useState<AdminTab>("dashboard");

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center text-slate-400 text-sm">
        Carregando...
      </div>
    );
  }
  if (!user) return <LoginScreen onOpenSubscribe={async () => {}} />;

  if (!isAdminEmail(user.email)) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center gap-6 px-6">
        <div className="w-16 h-16 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center">
          <ShieldOff className="w-8 h-8 text-red-400" />
        </div>
        <div className="text-center">
          <h1 className="text-xl font-black text-white mb-2">Acesso restrito.</h1>
          <p className="text-sm text-slate-400">Você não tem permissão para acessar esta área.</p>
        </div>
        <a href="/" className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-800/60 hover:bg-slate-800 px-5 py-2.5 text-sm font-bold text-slate-200 transition-colors">
          Voltar ao início
        </a>
      </div>
    );
  }

  const tabs: { id: AdminTab; label: string; icon: React.ReactNode }[] = [
    { id: "dashboard", label: "Dashboard", icon: <LayoutDashboard className="w-4 h-4" /> },
    { id: "support_beta", label: "Suporte Beta", icon: <Headphones className="w-4 h-4" /> },
    { id: "finance", label: "Financeiro", icon: <Wallet className="w-4 h-4" /> },
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* ─ Header ─ */}
      <header className="sticky top-0 z-30 border-b border-slate-800/70 bg-slate-950/90 backdrop-blur px-6 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-cyan-400/80">OrthoPrecision</p>
            <h1 className="text-lg font-black text-white leading-tight">Painel Administrativo</h1>
          </div>
          <nav className="flex items-center gap-1 bg-slate-900/60 rounded-2xl border border-slate-800/60 p-1">
            {tabs.map((tab) => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-bold transition-all ${
                  activeTab === tab.id
                    ? "bg-slate-800 text-white shadow-sm"
                    : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/50"
                }`}>
                {tab.icon}{tab.label}
              </button>
            ))}
          </nav>
          <button onClick={() => logout()}
            className="inline-flex items-center gap-2 rounded-xl border border-red-500/20 bg-red-500/10 hover:bg-red-500/15 px-4 py-2 text-sm font-bold text-red-300 transition-colors">
            <LogOut className="w-4 h-4" />
            Sair
          </button>
        </div>
      </header>

      {/* ─ Content ─ */}
      <main className="max-w-7xl mx-auto px-6 py-6">
        {activeTab === "dashboard" && <DashboardTab onNavigate={setActiveTab} />}
        {activeTab === "support_beta" && <AdminSupportBetaPanel />}
        {activeTab === "finance" && <AdminFinancePanel />}
      </main>
    </div>
  );
};

export default AdminPanel;
